import React, { useEffect, useState } from "react";
import "./CampaignList.scss";
import { ICampaign, ICampaignList } from "@utils/Interfaces";
import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Empty,
  MenuProps,
  Row,
  Tooltip,
} from "antd";
import Pipeline from "@components/Pipeline/Pipeline";
import {
  DeleteOutlined,
  EllipsisOutlined,
  EyeOutlined,
  InboxOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import MiniPipeline from "@components/MiniPipeline/MiniPipeline";
import businessLogicApi from "services/BusinessLogicApi";
import Helpers from "@utils/Helpers";

const CampaignList: React.FC<ICampaignList> = ({
  campaignList,
  setCampaignList,
  setIsCreateNew,
}) => {
  const [selectedCampaign, setSelectedCampaign] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deletedCampaign, setDeletedCampaign] = useState<number[]>([]);
  const notification = Helpers.notification;

  const handleDeleteCampaign = async () => {
    try {
      const response = await businessLogicApi.DeleteCampaign(selectedCampaign);
      if (response.status == 200) {
        const filteredCampaign = campaignList.filter(
          (item, index) => !response.data.includes(item.campaign_id)
        );
        setDeletedCampaign(response.data);
        setSelectedCampaign([]);
        setSelectAll(false);
        setTimeout(() => {
          setCampaignList(filteredCampaign);
        }, 500);
      }
    } catch (error) {
      notification.error("Something wrong in server. Please try again");
    }
  };

  return (
    <div className="CampaignList">
      <div className="card header">
        <div
          className="card-header d-flex align-items-center"
          id="card-campaign"
        >
          <div className="group-header">
            <div className="title-group">
              <div className="page-title">Campaign List</div>
              <div className="page-subtitle">
                Manage your candidate search campaigns
              </div>
            </div>
            <div className="action-header">
              <Button
                className="create-campaign-btn"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateNew(true)}
              >
                Campaign
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="search-filter-group bg-transparent">
        <div className="input-container w-25 me-auto">
          <span className="prefix">
            <i className="fa-solid fa-magnifying-glass"></i>
          </span>
          <input
            type="text"
            className="form-control-search"
            placeholder="Search"
          />
        </div>
        <div className="group-action gap-2">
          <div className="delete-action d-inline ms-4">
            <Button
              onClick={handleDeleteCampaign}
              color="danger"
              icon={<DeleteOutlined />}
              variant="link"
              disabled={selectedCampaign.length == 0}
            >
              Remove
            </Button>
          </div>
          <div className="sort-action d-inline">
            <Button
              color="default"
              variant="link"
              icon={<i className="fa-solid fa-arrow-down-wide-short"></i>}
            >
              Sort
            </Button>
          </div>
          <div className="filter-action d-inline gap-2">
            <Button
              color="default"
              variant="link"
              icon={<i className="fa-solid fa-filter"></i>}
            >
              Filter
            </Button>
          </div>
        </div>
      </div>
      <div className="campaign-table mt-4">
        <div className="table-titles">
          <Row>
            <Col span={5}>
              <Checkbox
                checked={selectAll}
                onChange={(e) => {
                  if (e.target.checked) {
                    const campaignIdArr: any[] = [];
                    campaignList.forEach((item: any) => {
                      campaignIdArr.push(item.campaign_id);
                    });
                    setSelectedCampaign(campaignIdArr);
                    setSelectAll(true);
                  } else {
                    setSelectedCampaign([]);
                    setSelectAll(false);
                  }
                }}
              />
              <span className="ms-2">Status</span>
            </Col>
            <Col span={4}>Name</Col>
            <Col span={4}>Start Date</Col>
            <Col span={4}>End Date</Col>
            <Col span={3}>Profile</Col>
            <Col span={3}>Budget</Col>
            <Col span={1}></Col>
          </Row>
        </div>
        {campaignList.length == 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          campaignList.map((item: ICampaign, index: number) => {
            return (
              <CampaignItem
                item={item}
                index={index}
                key={index}
                selectedCampaign={selectedCampaign}
                setSelectedCampaign={setSelectedCampaign}
                deletedCampaign={deletedCampaign}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

const CampaignItem: React.FC<any> = ({
  item,
  index,
  selectedCampaign,
  setSelectedCampaign,
  deletedCampaign,
}) => {
  const [presentNavigation, setPreventNavigation] = useState(false);
  const items: MenuProps["items"] = [
    {
      label: <div>New Version</div>,
      key: "0",
    },
    {
      label: <div>Rename</div>,
      key: "1",
    },
    {
      label: <div>Unarchive</div>,
      key: "2",
    },
    {
      label: <div>Pause</div>,
      key: "3",
    },
  ];

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = String(date.getUTCFullYear());
    return `${day}-${month}-${year}`;
  };

  const handleActionClick = (e: any) => {
    e.stopPropagation();
  };

  return (
    <div
      className={`CampaignItem item-${item.campaign_id} ${
        deletedCampaign.includes(item.campaign_id) ? "fade-out" : ""
      }`}
      key={index}
      onClick={() => {
        if (!presentNavigation) {
          Helpers.redirect(`/recruiter/panel/sourcing?id=${item.campaign_id}`);
        }
      }}
    >
      <div className="campaign-item-wrap">
        <div className="campaign-info">
          <Row>
            <Col span={5}>
              <div className="action-group">
                <Checkbox
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCampaign([
                        ...selectedCampaign,
                        item.campaign_id,
                      ]);
                    } else {
                      const filterdCampaign = selectedCampaign.filter(
                        (ite: number, index: number) => ite !== item.campaign_id
                      );
                      setSelectedCampaign(filterdCampaign);
                    }
                  }}
                  checked={selectedCampaign.includes(item.campaign_id)}
                />
                <Tooltip placement="top" title={"active"}>
                  <div className="status-dot active"></div>
                </Tooltip>
                <div className="action-icon-group">
                  <Tooltip placement="top" title={"Pause"}>
                    <PauseCircleOutlined
                      className="action-icon"
                      onClick={handleActionClick}
                    />
                  </Tooltip>
                  <Tooltip placement="top" title={"Continue"}>
                    <PlayCircleOutlined
                      className="action-icon"
                      onClick={handleActionClick}
                    />
                  </Tooltip>
                  <Tooltip placement="top" title={"Stop"}>
                    <StopOutlined
                      className="action-icon"
                      onClick={handleActionClick}
                    />
                  </Tooltip>
                  <Tooltip placement="top" title={"Archive"}>
                    <InboxOutlined
                      className="action-icon"
                      onClick={handleActionClick}
                    />
                  </Tooltip>
                </div>
              </div>
            </Col>
            <Col span={4}>
              <div className="campaign-name">{item.campaign_name}</div>
            </Col>
            <Col span={4}>
              <div className="campaign-start-date">
                {formatDate(item.start_date)}
              </div>
            </Col>
            <Col span={4}>
              <div className="campaign-end-date">
                {formatDate(item.end_date)}
              </div>
            </Col>
            <Col span={3}>
              <div className="campaign-profile">32/65</div>
            </Col>
            <Col span={3}>
              <div className="campaign-budget">200/300</div>
            </Col>
            <Col span={1}>
              <Dropdown
                onOpenChange={(open) => {
                  if (open) {
                    setPreventNavigation(true);
                  } else {
                    setPreventNavigation(false);
                  }
                }}
                menu={{ items }}
                trigger={["click"]}
              >
                <div onClick={(e: any) => e.stopPropagation()}>
                  <EllipsisOutlined />
                </div>
              </Dropdown>
            </Col>
          </Row>
        </div>
        <div className="campaign-pipline">
          <MiniPipeline activePhase={item.phase} phases={item.phases} />
        </div>
      </div>
    </div>
  );
};

export default CampaignList;
